"use client";

import {
  ArrowDown,
  Loader2,
  Minus,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useState, useTransition } from "react";
import { validateDiscountCode } from "@/app/(users)/totem-by-slug/[slug]/actions/validate-discount-code";
import { useTotem } from "@/app/(users)/totem-by-slug/[slug]/providers/totem-provider";
import { getProductStockLimit } from "@/app/(users)/totem-by-slug/[slug]/utils/product-stock";
import {
  calculateCartItemTotal,
  formatTotemPrice,
} from "@/app/(users)/totem-by-slug/[slug]/utils/totem-price";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export function TotemOrderPanel() {
  return (
    <>
      {/* Desktop view */}
      <div className="hidden lg:flex h-full">
        <DesktopOrderPanelContent />
      </div>
      {/* Mobile view (Drawer) */}
      <div className="flex lg:hidden fixed bottom-0 left-0 w-full z-50 shadow-[0_-10px_30px_rgba(0,0,0,0.15)]">
        <MobileOrderDrawer />
      </div>
    </>
  );
}

function DesktopOrderPanelContent() {
  const {
    totem,
    cart,
    setCheckoutOpen,
    menu,
    setSelectedProduct,
    setEditingCartItemId,
    desktopCheckoutRef,
    orderType,
    setOrderOptionsOpen,
    setOrderType,
    setPickupMethod,
    setBeeperNumber,
  } = useTotem();

  const [discountCode, setDiscountCode] = useState("");
  const [discountError, setDiscountError] = useState("");
  const [isPending, startTransition] = useTransition();

  const handleApplyDiscount = () => {
    if (!discountCode.trim() || !totem?.location_id) return;

    setDiscountError("");
    startTransition(async () => {
      const result = await validateDiscountCode(
        discountCode.trim(),
        totem.location_id as string,
        cart.subtotal,
      );
      if (result.success && result.discount) {
        cart.applyDiscount({
          id: result.discount.id,
          code: result.discount.code,
          amount: result.discount.amount,
          type: result.discount.type === "percentage" ? "percentage" : "fixed",
          method: result.discount.method,
          minPurchase: result.discount.minPurchase,
        });
        setDiscountCode("");
      } else {
        setDiscountError(result.message || "Código inválido");
      }
    });
  };

  return (
    <aside className="flex flex-col w-[350px] shrink-0 bg-background dark:bg-[#2C2C2E] border-l border-border dark:border-zinc-800 h-full shadow-[-4px_0_15px_-3px_rgba(0,0,0,0.03)] z-10">
      {/* Header */}
      <div
        ref={desktopCheckoutRef as React.RefObject<HTMLDivElement>}
        className="flex flex-col gap-5 shrink-0 px-6 py-6 border-b border-border dark:border-zinc-800"
      >
        <div className="flex items-center bg-muted/40 dark:bg-zinc-800/40 p-1 rounded-xl w-full">
          <button
            type="button"
            onClick={() => {
              setOrderType("takeaway");
              setPickupMethod(null);
              setBeeperNumber("");
            }}
            className={cn(
              "flex-1 px-3 py-2 rounded-lg text-[13px] font-bold transition-all",
              orderType === "takeaway" || !orderType
                ? "bg-zinc-200 dark:bg-[#4a4a4c] text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            Para llevar
          </button>
          <button
            type="button"
            onClick={() => {
              setOrderType("eat_here");
              setOrderOptionsOpen(true);
            }}
            className={cn(
              "flex-1 px-3 py-2 rounded-lg text-[13px] font-bold transition-all",
              orderType === "eat_here"
                ? "bg-zinc-200 dark:bg-[#4a4a4c] text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            Comer aquí
          </button>
        </div>

        <div className="flex items-center justify-between w-full">
          <h2 className="text-2xl font-bold tracking-tight text-foreground dark:text-white leading-none">
            Tu pedido
          </h2>
          <div className="flex flex-col items-end">
            <span className="text-lg font-bold text-foreground dark:text-white leading-none">
              {cart.itemCount}
            </span>
            <span className="text-sm font-medium text-muted-foreground dark:text-zinc-400">
              productos
            </span>
          </div>
        </div>
      </div>

      {/* Discount Code Section */}
      <div className="px-6 py-4 border-b border-border dark:border-zinc-800 bg-muted/20 dark:bg-zinc-800/20">
        <div className="flex items-center w-full gap-2 p-1.5 rounded-xl border border-border dark:border-zinc-700 bg-background dark:bg-[#3a3a3a]">
          <Input
            placeholder="Código de descuento"
            className="border-none bg-transparent h-9 text-sm text-foreground dark:text-white placeholder:text-muted-foreground dark:placeholder:text-zinc-500 focus-visible:ring-0 shadow-none font-semibold px-3"
            value={discountCode}
            onChange={(e) => {
              setDiscountCode(e.target.value);
              setDiscountError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleApplyDiscount();
            }}
            disabled={isPending || cart.discount !== null}
          />
          {cart.discount ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-red-400 hover:text-red-300 hover:bg-transparent font-bold px-4 h-9"
              onClick={() => cart.clearDiscount()}
            >
              Quitar
            </Button>
          ) : (
            <Button
              size="sm"
              className="bg-[#dce3fe] hover:bg-[#c9d3fd] text-[#2c2c2e] font-bold rounded-lg px-4 h-9"
              onClick={handleApplyDiscount}
              disabled={isPending || !discountCode.trim()}
            >
              {isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                "Aplicar"
              )}
            </Button>
          )}
        </div>
        {discountError && (
          <p className="text-red-400 text-xs mt-2 font-medium px-2">
            {discountError}
          </p>
        )}
        {cart.discount && (
          <p className="text-emerald-500 dark:text-emerald-400 text-xs mt-2 font-medium px-2">
            Descuento aplicado: {cart.discount.code}
            {cart.discount.type === "percentage"
              ? ` (${cart.discount.amount}%)`
              : ` (-${formatTotemPrice(cart.discount.amount)})`}
          </p>
        )}
      </div>

      {/* Content - Items List */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 py-2">
        {cart.items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center text-muted-foreground dark:text-zinc-500">
            <ShoppingCartIcon className="size-16 opacity-30" />
            <p className="text-lg font-medium">Tu pedido está vacío</p>
          </div>
        ) : (
          <ul className="flex flex-col">
            {cart.items.map((item) => {
              const lineTotal = calculateCartItemTotal(item);
              const product = menu?.products.find(
                (p) => p.id === item.productId,
              );
              const stockLimit = getProductStockLimit(product?.product_stock);
              const totalProductQuantity = cart.items
                .filter((i) => i.productId === item.productId)
                .reduce((sum, i) => sum + i.quantity, 0);
              const isAtStockLimit = totalProductQuantity >= stockLimit;
              const hasOptions =
                item.subselections.length > 0 ||
                (product?.product_subselection &&
                  product.product_subselection.length > 0);

              return (
                <li
                  key={item.id}
                  className="flex flex-col gap-2 border-b border-border dark:border-zinc-800 py-5"
                >
                  <div className="flex gap-4">
                    {/* Left Side: Avatar, Title, Price */}
                    <div className="flex flex-1 gap-3 min-w-0">
                      <Avatar className="size-14 shrink-0 rounded-xl border border-border dark:border-zinc-700 bg-muted dark:bg-zinc-800">
                        <AvatarImage
                          alt={item.name}
                          src={item.imageUrl}
                          className="object-cover"
                        />
                        <AvatarFallback className="rounded-xl bg-muted dark:bg-zinc-800 text-foreground dark:text-white font-bold">
                          {item.name.slice(0, 1)}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex flex-col min-w-0 flex-1 gap-0.5 pt-0.5">
                        <span className="truncate text-base font-bold leading-tight text-foreground dark:text-white">
                          {item.name}
                        </span>
                        <span className="text-base font-bold text-orange-500">
                          {formatTotemPrice(lineTotal)}
                        </span>
                      </div>
                    </div>

                    {/* Right Side: 2x2 Buttons Grid */}
                    <div className="flex flex-col gap-2 shrink-0 items-end">
                      {/* Row 1: Qty [-] [1] [+] */}
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="flex items-center justify-center shrink-0 size-9 rounded-full bg-red-500 hover:bg-red-600 text-white shadow-none border-none transition-colors"
                          onClick={() => {
                            if (item.quantity > 1) {
                              cart.updateQuantity(item.id, item.quantity - 1);
                            } else {
                              cart.removeItem(item.id);
                            }
                          }}
                        >
                          <Minus className="size-4 stroke-[3]" />
                        </button>
                        <div className="size-9 flex items-center justify-center bg-muted dark:bg-[#3a3a3a] border border-border dark:border-zinc-700 rounded-xl">
                          <span className="text-lg font-bold text-foreground dark:text-white">
                            {item.quantity}
                          </span>
                        </div>
                        <button
                          type="button"
                          disabled={isAtStockLimit}
                          className="flex items-center justify-center shrink-0 size-9 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white shadow-none border-none transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          onClick={() => {
                            if (!isAtStockLimit) {
                              cart.updateQuantity(item.id, item.quantity + 1);
                            }
                          }}
                        >
                          <Plus className="size-4 stroke-[3]" />
                        </button>
                      </div>

                      {/* Row 2: Edit & Trash */}
                      <div className="flex items-center gap-2">
                        {hasOptions && (
                          <button
                            type="button"
                            className="flex items-center justify-center shrink-0 size-9 rounded-full bg-orange-500 hover:bg-orange-600 text-white shadow-none border-none transition-colors"
                            onClick={() => {
                              if (product) {
                                setEditingCartItemId(item.id);
                                setSelectedProduct(product);
                              }
                            }}
                          >
                            <Pencil className="size-3.5" />
                          </button>
                        )}

                        <button
                          type="button"
                          className="flex items-center justify-center shrink-0 size-9 rounded-full bg-red-500 hover:bg-red-600 text-white shadow-none border-none transition-colors"
                          onClick={() => cart.removeItem(item.id)}
                        >
                          <X className="size-4 stroke-[3]" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Subselections Full Width Row Below */}
                  {item.subselections.length > 0 && (
                    <div className="flex flex-col mt-1 gap-1 pl-2.5 ml-[68px] border-l-2 border-emerald-500/20">
                      {item.subselections.map((sub) => (
                        <span
                          key={sub.subselectionItemId}
                          className="text-[13px] font-medium leading-snug text-emerald-600 dark:text-emerald-400 break-words whitespace-normal"
                        >
                          {sub.quantity > 1 ? `${sub.quantity}x ` : ""}
                          {sub.name}{" "}
                          {sub.price > 0
                            ? `(+${formatTotemPrice(sub.price)})`
                            : ""}
                        </span>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Footer */}
      <div className="shrink-0 px-6 py-6 border-t border-border dark:border-zinc-800 bg-background dark:bg-[#2C2C2E] flex flex-col gap-5">
        <div className="flex items-center justify-between text-foreground dark:text-white">
          <span className="text-xl font-bold">Total</span>
          <div className="flex items-start gap-1 text-orange-500">
            <span className="text-lg font-bold mt-0.5">$</span>
            <span className="text-3xl font-black tracking-tight">
              {new Intl.NumberFormat("es-AR", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              }).format(Math.trunc(cart.total * 100) / 100)}
            </span>
          </div>
        </div>

        <div className="flex gap-3 h-14">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="size-14 shrink-0 bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/20 text-orange-500 hover:text-orange-600 hover:bg-red-100 dark:hover:bg-red-500/20 hover:border-red-300 dark:hover:border-red-500/30 rounded-2xl shadow-none p-0"
                disabled={cart.items.length === 0}
              >
                <Trash2 className="size-6" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Vaciar el pedido?</AlertDialogTitle>
                <AlertDialogDescription>
                  Se eliminarán todos los productos de tu pedido actual. Esta
                  acción no se puede deshacer.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Volver</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => {
                    cart.clearCart();
                    setOrderType(null);
                    setPickupMethod(null);
                    setBeeperNumber("");
                  }}
                >
                  Vaciar pedido
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Button
            className="flex-1 bg-[#3ba859] hover:bg-[#34964e] text-white font-bold text-xl rounded-2xl h-full shadow-lg hover:shadow-xl transition-all active:scale-[0.98]"
            disabled={cart.items.length === 0}
            onClick={() => setTimeout(() => setCheckoutOpen(true), 150)}
          >
            Cobrar
          </Button>
        </div>
      </div>
    </aside>
  );
}

function ShoppingCartIcon(props: React.ComponentProps<"svg">) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z"
      />
    </svg>
  );
}

function MobileOrderDrawer() {
  const {
    totem,
    cart,
    setCheckoutOpen,
    menu,
    setSelectedProduct,
    setEditingCartItemId,
    setOrderType,
    setPickupMethod,
    setBeeperNumber,
    mobileCheckoutRef,
  } = useTotem();

  const [discountCode, setDiscountCode] = useState("");
  const [discountError, setDiscountError] = useState("");
  const [isPending, startTransition] = useTransition();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 640px)");
    const handleResize = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches && open) {
        setOpen(false);
      }
    };
    handleResize(mediaQuery);
    mediaQuery.addEventListener("change", handleResize);
    return () => mediaQuery.removeEventListener("change", handleResize);
  }, [open]);

  const handleApplyDiscount = () => {
    if (!discountCode.trim() || !totem?.location_id) return;

    setDiscountError("");
    startTransition(async () => {
      const result = await validateDiscountCode(
        discountCode.trim(),
        totem.location_id as string,
        cart.subtotal,
      );
      if (result.success && result.discount) {
        cart.applyDiscount({
          id: result.discount.id,
          code: result.discount.code,
          amount: result.discount.amount,
          type: result.discount.type === "percentage" ? "percentage" : "fixed",
          method: result.discount.method,
          minPurchase: result.discount.minPurchase,
        });
        setDiscountCode("");
      } else {
        setDiscountError(result.message || "Código inválido");
      }
    });
  };

  const truncatedTotal = Math.trunc(cart.total * 100) / 100;
  const hasDecimals = truncatedTotal % 1 !== 0;
  const formattedTotal = new Intl.NumberFormat("es-AR", {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(truncatedTotal);

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <button
          ref={mobileCheckoutRef as React.RefObject<HTMLButtonElement>}
          type="button"
          className="relative flex w-full flex-col items-end justify-end bg-[#353535] px-4 pb-3 pt-6 transition-transform active:scale-[0.98]"
        >
          {/* Fake rounded corners: sits inside the black bar so the black background is visible behind the curves */}
          <div className="absolute left-0 right-0 top-0 h-6 bg-[#e5e5e5] rounded-b-[32px]" />

          <div className="flex items-start gap-3 relative z-10 pt-2">
            <div className="flex flex-col items-start">
              <div className="flex h-11 items-center justify-start rounded-lg bg-[#c8d4fc] px-4 min-w-[90px]">
                <span className="text-xl font-black text-[#2c2c2e] leading-none">
                  ${formattedTotal}
                </span>
              </div>
              <span className="text-[11px] font-bold text-white mt-1 pl-4">
                Total
              </span>
            </div>

            <div className="flex h-11 items-center justify-center rounded-lg bg-[#4fd16d] px-6 text-base font-bold text-black shadow-sm">
              Pagar
            </div>
          </div>
        </button>
      </DrawerTrigger>

      <DrawerContent className="bg-transparent border-none text-white h-[100dvh] !max-h-none !mt-0 flex flex-col p-0 [&>div.mx-auto]:hidden">
        <DrawerTitle className="sr-only">Tu pedido</DrawerTitle>
        <div className="shrink-0 pt-2 pb-2 px-4 bg-[#e5e5e5] rounded-b-[24px] flex justify-end relative z-20">
          <DrawerClose asChild>
            <button
              type="button"
              className="flex items-center gap-2 rounded-lg bg-[#c8d4fc] px-4 py-2 font-bold text-black transition-opacity hover:opacity-90"
            >
              <span className="text-[15px]">Volver al inicio</span>
              <ArrowDown className="size-5" />
            </button>
          </DrawerClose>
        </div>

        <div className="flex flex-col min-h-0 flex-1 px-6 bg-[#353535] -mt-6 relative z-10">
          <div className="shrink-0 pt-10 pb-6">
            <label
              htmlFor="discount-code-mobile"
              className="text-sm font-medium text-[#e0e0e0] mb-2 block"
            >
              Ingresá tu código de descuento
            </label>
            <div className="flex items-center w-full gap-2 p-1.5 rounded-xl border border-zinc-600 bg-[#2c2c2e]">
              <Input
                id="discount-code-mobile"
                placeholder="CÓDIGO DE DESCUENTO"
                className="border-none bg-transparent h-9 text-sm text-white placeholder:text-zinc-500 focus-visible:ring-0 shadow-none font-semibold px-3 uppercase"
                value={discountCode}
                onChange={(e) => {
                  setDiscountCode(e.target.value);
                  setDiscountError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleApplyDiscount();
                }}
                disabled={isPending || cart.discount !== null}
              />
              {cart.discount ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-400 hover:text-red-300 hover:bg-transparent font-bold px-4 h-9"
                  onClick={() => cart.clearDiscount()}
                >
                  Quitar
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="bg-[#c8d4fc] hover:bg-[#b0c0f9] text-[#2c2c2e] font-bold rounded-lg px-4 h-9"
                  onClick={handleApplyDiscount}
                  disabled={isPending || !discountCode.trim()}
                >
                  {isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    "Aplicar"
                  )}
                </Button>
              )}
            </div>
            {discountError && (
              <p className="text-red-400 text-xs mt-2 font-medium px-2">
                {discountError}
              </p>
            )}
            {cart.discount && (
              <p className="text-emerald-400 text-xs mt-2 font-medium px-2">
                Descuento aplicado: {cart.discount.code}
                {cart.discount.type === "percentage"
                  ? ` (${cart.discount.amount}%)`
                  : ` (-${formatTotemPrice(cart.discount.amount)})`}
              </p>
            )}
          </div>

          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Tu pedido</h2>
            <span className="text-sm text-zinc-400">
              {cart.itemCount} producto{cart.itemCount !== 1 && "s"}
            </span>
          </div>

          <div className="flex-1 overflow-y-auto pb-4">
            {cart.items.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-4 py-12 text-center text-zinc-500">
                <ShoppingCartIcon className="size-16 opacity-30" />
                <p className="text-lg font-medium">Tu pedido está vacío</p>
              </div>
            ) : (
              <ul className="flex flex-col gap-6">
                {cart.items.map((item) => {
                  const lineTotal = calculateCartItemTotal(item);
                  const product = menu?.products.find(
                    (p) => p.id === item.productId,
                  );
                  const stockLimit = getProductStockLimit(
                    product?.product_stock,
                  );
                  const totalProductQuantity = cart.items
                    .filter((i) => i.productId === item.productId)
                    .reduce((sum, i) => sum + i.quantity, 0);
                  const isAtStockLimit = totalProductQuantity >= stockLimit;
                  const hasOptions =
                    item.subselections.length > 0 ||
                    (product?.product_subselection &&
                      product.product_subselection.length > 0);

                  return (
                    <li key={item.id} className="flex flex-col gap-2">
                      <div className="flex items-center gap-4">
                        <Avatar className="size-12 shrink-0 rounded-full bg-white">
                          <AvatarImage
                            alt={item.name}
                            src={item.imageUrl}
                            className="object-contain p-1"
                          />
                          <AvatarFallback className="rounded-full bg-white text-black font-bold">
                            {item.name.slice(0, 1)}
                          </AvatarFallback>
                        </Avatar>

                        <div className="flex flex-col min-w-0 flex-1">
                          <span className="truncate text-base font-bold text-white">
                            {item.name}
                          </span>
                          <span className="text-sm font-medium text-zinc-400">
                            AGUA RUMIPAL BIDON 6L{" "}
                            {/* TODO: subtitle if available */}
                          </span>
                          <span className="text-base font-bold text-white mt-0.5">
                            {formatTotemPrice(lineTotal)}
                          </span>
                        </div>

                        <div className="flex flex-col gap-2 shrink-0 items-end">
                          <div className="flex items-center gap-2">
                            {hasOptions && (
                              <button
                                type="button"
                                className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#ff9800] text-white"
                                onClick={() => {
                                  if (product) {
                                    setEditingCartItemId(item.id);
                                    setSelectedProduct(product);
                                    setOpen(false);
                                  }
                                }}
                              >
                                <Pencil className="size-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#ef5350] text-white"
                              onClick={() => cart.removeItem(item.id)}
                            >
                              <X className="size-4" />
                            </button>
                          </div>

                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#ef5350] text-white"
                              onClick={() => {
                                if (item.quantity > 1) {
                                  cart.updateQuantity(
                                    item.id,
                                    item.quantity - 1,
                                  );
                                } else {
                                  cart.removeItem(item.id);
                                }
                              }}
                            >
                              <Minus className="size-4" />
                            </button>
                            <div className="flex size-8 items-center justify-center rounded-xl border border-white/50 bg-transparent text-white font-bold">
                              {item.quantity}
                            </div>
                            <button
                              type="button"
                              disabled={isAtStockLimit}
                              className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#4caf50] text-white disabled:opacity-40 disabled:cursor-not-allowed"
                              onClick={() => {
                                if (!isAtStockLimit) {
                                  cart.updateQuantity(
                                    item.id,
                                    item.quantity + 1,
                                  );
                                }
                              }}
                            >
                              <Plus className="size-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="shrink-0 bg-[#353535] px-6 py-6 pb-8">
          <div className="flex items-center gap-4 mb-6">
            <span className="text-xl font-bold text-white">Total</span>
            <div className="h-[1px] flex-1 bg-zinc-600" />
            <span className="text-2xl font-black text-white">
              ${formattedTotal}
            </span>
          </div>

          <div className="flex gap-4 h-14">
            <Button
              className="flex-1 bg-[#ff6b6b] hover:bg-[#ff5252] text-white font-bold text-lg rounded-2xl h-full"
              disabled={cart.items.length === 0}
              onClick={() => {
                cart.clearCart();
                setOrderType(null);
                setPickupMethod(null);
                setBeeperNumber("");
                setOpen(false);
              }}
            >
              <Trash2 className="size-5 mr-2" /> Cancelar
            </Button>
            <Button
              className="flex-1 bg-[#4fd16d] hover:bg-[#45b960] text-white font-bold text-lg rounded-2xl h-full"
              disabled={cart.items.length === 0}
              onClick={() => {
                setOpen(false);
                setTimeout(() => setCheckoutOpen(true), 150);
              }}
            >
              Pagar
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
